import { createContext, useContext, useState, useMemo } from "react";

const UserContext = createContext(null);

const initialState = {
  isAuthenticated: !!localStorage.getItem("accessToken"),
  user: localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null,
  accessToken: localStorage.getItem("accessToken") ?? null,
  refreshToken: localStorage.getItem("refreshToken") ?? null,
  admin: JSON.parse(localStorage.getItem("admin")) ?? null,
  isSuperAdmin: !!localStorage.getItem("admin"),
  isImpersonating: localStorage.getItem("isImpersonating")
    ? JSON.parse(localStorage.getItem("isImpersonating"))
    : null,
  subscriptionDetail: localStorage.getItem("subscriptionDetail")
    ? JSON.parse(localStorage.getItem("subscriptionDetail"))
    : {
        type: "free",
        canceledAt: null,
        trialEndDate: null,
        status: "free",
      },
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(initialState);

  const login = (userData) => {
    const { access_token, refreshToken, ...user } = userData;
    setUser((pre) => {
      return {
        ...pre,
        accessToken: access_token,
        refreshToken,
        isAuthenticated: true,
        user: user,
        isSuperAdmin: user?.isSuperAdmin ? true : false,
      };
    });
    if (user?.isSuperAdmin) {
      localStorage.setItem("admin", JSON.stringify(user));
    }
    localStorage.setItem("accessToken", access_token);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("isImpersonating", null);
  };

  const logout = () => {
    localStorage.clear();
    setUser(initialState);
  };

  const updateUser = (
    userData,
    isImpersonating = user?.isImpersonating,
    isSuperAdmin = user?.isSuperAdmin,
    access_token = user?.accessToken,
    refreshToken = user?.refreshToken,
    subscriptionDetail = user?.subscriptionDetail
  ) => {
    setUser((pre) => {
      return {
        ...pre,
        isSuperAdmin,
        user: userData,
        accessToken: access_token,
        refreshToken: refreshToken,
        isImpersonating,
        subscriptionDetail,
      };
    });
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const impersonate = (data) => {
    const adminAccessToken = localStorage.getItem("accessToken");
    const adminRefreshToken = localStorage.getItem("refreshToken");
    localStorage.setItem("accessToken", data?.access_token);
    localStorage.setItem("refreshToken", data?.refreshToken);
    const userValue = localStorage.getItem("user");
    const parsedUserValue = userValue ? JSON.parse(userValue) : null;
    const object = {
      access_token: adminAccessToken,
      refreshToken: adminRefreshToken,
      user: parsedUserValue,
    };
    localStorage.setItem("admin", JSON.stringify(object));
    localStorage.setItem("user", JSON.stringify(data));
    localStorage.setItem("isImpersonating", JSON.stringify(user));
    updateUser(data, parsedUserValue);
  };

  const stopImpersonate = () => {
    const admin = localStorage.getItem("admin");
    const parsedAdminValue = admin ? JSON.parse(admin) : null;
    localStorage.setItem("accessToken", parsedAdminValue?.access_token);
    localStorage.setItem("refreshToken", parsedAdminValue?.refreshToken);
    localStorage.setItem("user", JSON.stringify(parsedAdminValue?.user));
    const isSuperAdmin = parsedAdminValue?.user?.isSuperAdmin || false;
    updateUser(
      parsedAdminValue.user,
      null,
      isSuperAdmin,
      parsedAdminValue.access_token,
      parsedAdminValue.refreshToken
    );
    localStorage.setItem("isImpersonating", null);
  };

  const updateSubscription = (newSubscription) => {
    const userFromStorage = localStorage.getItem("user");
    const parsedUser = userFromStorage ? JSON.parse(userFromStorage) : null;
    if (parsedUser) {
      parsedUser.account.subscription = newSubscription.type;
      localStorage.setItem("user", JSON.stringify(parsedUser));
      localStorage.setItem("subscriptionDetail", JSON.stringify(newSubscription));
      updateUser(parsedUser, undefined, undefined, undefined, undefined, newSubscription);
    }
  };

  const updateAccount = (account) => {
    const userFromStorage = localStorage.getItem("user");
    const parsedUser = userFromStorage ? JSON.parse(userFromStorage) : null;
    if (parsedUser) {
      parsedUser.account = account;
      localStorage.setItem("user", JSON.stringify(parsedUser));
      updateUser(parsedUser);
    }
  };

  const getSubscription = () => user?.subscriptionDetail;

  const getAccount = () => user?.user?.account;

  const getAccountId = () => user?.user?.account?.id;

  const getUserEmail = () => user?.user?.email;

  const getDistributorId = () => user?.user?.account?.distributor?.id ?? null;

  const getBrandId = () => user?.user?.account?.distributor?.id ?? null;

  const contextValue = useMemo(
    () => ({
      user,
      login,
      logout,
      updateUser,
      impersonate,
      stopImpersonate,
      getAccountId,
      updateSubscription,
      updateAccount,
      getSubscription,
      getUserEmail,
      getAccount,
      getDistributorId,
      getBrandId,
    }),
    [user]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
